<template>
  <div class="marker-content">
    <div v-if="items.length === 1">
      <ResultItem
        :item="items[0]"
        :job-search-global="jobSearchGlobal"
        @click="emitItemClicked(items[0])"
      />
    </div>
    <div v-else>
      <div v-if="slider && items.length > 1" class="slider-controls">
        <button
          :class="{
            'control-button': true,
            'control-button--prev': true,
            'control-button--disabled': current === 0,
          }"
          aria-label="Previous"
          @click="handlePrevSlide"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            width="16"
            height="16"
          >
            <path
              fill="currentColor"
              d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"
            />
          </svg>
        </button>

        <div class="slide-counter">{{ current + 1 }}/{{ items.length }}</div>

        <button
          :class="{
            'control-button': true,
            'control-button--next': true,
            'control-button--disabled': isLastSlide,
          }"
          aria-label="Next"
          @click="handleNextSlide"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            width="16"
            height="16"
          >
            <path
              fill="currentColor"
              d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"
            />
          </svg>
        </button>
      </div>

      <div ref="slider" class="keen-slider">
        <div v-for="item in items" :key="item.id" class="keen-slider__slide">
          <ResultItem
            :item="item"
            :job-search-global="jobSearchGlobal"
            @click="emitItemClicked(item)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ResultItem from "../../ResultItem/ResultItem.vue";
import KeenSlider from "keen-slider";

export default {
  name: "MarkerContent",
  components: { ResultItem },
  inject: ["popupState"],
  props: {
    items: {
      type: Array,
      required: true,
    },
    jobSearchGlobal: {
      type: Object,
      required: true,
    },
  },
  emits: ["item-clicked", "slide-changed"],
  data() {
    return {
      slider: null,
      current: 0,
    };
  },
  computed: {
    isLastSlide() {
      return (
        this.slider &&
        this.current === this.slider.track.details.slides.length - 1
      );
    },
    isVisible() {
      return this.popupState && this.popupState.isOpen;
    },
  },
  watch: {
    isVisible(newValue) {
      if (newValue) {
        // Popup is opening, initialize slider
        this.$nextTick(() => {
          this.initializeSlider();
        });
      } else {
        // Popup is closing, destroy slider
        this.destroySlider();
      }
    },
    items(newItems, oldItems) {
      const sameIds =
        oldItems &&
        newItems.length === oldItems.length &&
        newItems.every((item, i) => item.id === oldItems[i].id);

      const wasSingle = oldItems && oldItems.length <= 1;
      const isNowMultiple = newItems.length > 1;

      if (!sameIds || (wasSingle && isNowMultiple)) {
        this.$nextTick(() => {
          this.initializeSlider();
        });
      }

      if (this.slider && newItems.length <= 1) {
        this.destroySlider(); // cleanup if it went from multiple to 1
      }
    },
  },
  mounted() {
    if (this.isVisible) {
      this.$nextTick(() => {
        this.initializeSlider();
      });
    }
  },
  unmounted() {
    this.destroySlider();
  },
  destroyed() {
    this.destroySlider();
  },
  methods: {
    initializeSlider() {
      // Only initialize slider if there are multiple items
      if (this.items.length <= 1 || !this.$refs.slider) return;

      this.destroySlider();

      this.slider = new KeenSlider(this.$refs.slider, {
        mode: "free-snap",
        slides: {
          origin: "center",
          perView: 1,
          spacing: 20,
        },
        dragSpeed: 0.5,
        initial: 0,
        slideChanged: (s) => {
          this.current = s.track.details.rel;
        },
      });

      // Force recalculation of dimensions & wait for actual layout repaint
      requestAnimationFrame(() => {
        if (this.slider) this.slider.update();
      });
    },
    handlePrevSlide() {
      this.slider.prev();
      this.$emit("slide-changed", "prev");
    },
    handleNextSlide() {
      this.slider.next();
      this.$emit("slide-changed", "next");
    },
    destroySlider() {
      if (this.slider) {
        this.slider.destroy();
        this.slider = null;
        this.current = 0;
      }
    },
    emitItemClicked(item) {
      this.$emit("item-clicked", item);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "keen-slider/keen-slider.min.css";

.marker-content {
  position: relative;
  width: 100%;
}

.keen-slider {
  position: relative;
  width: 100%;
}

.slider-controls {
  display: flex;
  align-items: center;
  justify-content: center;

  width: fit-content;
  margin-right: auto;
  margin-bottom: 8px;
  margin-left: auto;
  padding: 4px 8px;

  background-color: rgb(255 255 255 / 90%);
  border-radius: 20px;
  box-shadow: 0 2px 4px rgb(0 0 0 / 10%);
}

.control-button {
  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: center;

  width: 24px;
  height: 24px;
  padding: 0;

  color: #333;

  background: transparent;
  border: none;

  &:hover:not(.control-button--disabled) {
    color: #000;
  }
}

.control-button--disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.slide-counter {
  @include font-family("body");
  @include scale-fonts("body-small");

  min-width: 30px;
  margin: -1px 8px 0;
  text-align: center;
}

.keen-slider__slide {
  width: 100%;
  min-width: 100%;
}
</style>
